import { useCallback, useState } from 'react';

/**
 * Unsaved progress on a long form, kept through a page refresh.
 *
 * `sessionStorage` rather than `localStorage`: a draft survives a reload or an
 * accidental F5, but is gone when the tab closes, so contact details typed into
 * a half-finished form do not sit on a shared office machine for weeks. Keys
 * should carry the signed-in user's uuid, so a second person signing in on the
 * same tab never opens the first one's draft — and signing out clears them all.
 *
 * Every access is guarded: storage can be full, disabled or blocked, and a form
 * that cannot remember is still a form that works.
 */

const PREFIX = 'draft:';

function read<T>(storageKey: string | null): T | null {
  if (!storageKey) return null;
  try {
    const raw = sessionStorage.getItem(storageKey);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

export function useSessionDraft<T>(key: string | null) {
  const storageKey = key ? `${PREFIX}${key}` : null;

  // Read once, on mount: the form takes it as its starting values and owns the
  // state from then on.
  const [initial] = useState(() => read<T>(storageKey));

  const save = useCallback(
    (value: T) => {
      if (!storageKey) return;
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(value));
      } catch {
        // Full or blocked — losing the draft is better than breaking the form.
      }
    },
    [storageKey],
  );

  const clear = useCallback(() => {
    if (!storageKey) return;
    try {
      sessionStorage.removeItem(storageKey);
    } catch {
      // Nothing to clear if storage cannot be reached.
    }
  }, [storageKey]);

  return { initial, save, clear };
}

/** Drops every draft in this tab. Called on sign-out. */
export function clearSessionDrafts() {
  try {
    Object.keys(sessionStorage)
      .filter((storageKey) => storageKey.startsWith(PREFIX))
      .forEach((storageKey) => sessionStorage.removeItem(storageKey));
  } catch {
    // Storage unavailable — there is nothing in it to clear.
  }
}
